import React, { useState } from 'react';
import { useAuth } from './hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import logo from './assets/logo.svg';
import monarkaLogo from './assets/hidden_gem.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  
  // Detect Monarka email
  const isMonarkaEmail = email.trim().toLowerCase().endsWith('@monarkaclinic.com');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password);
      navigate('/app');
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión. Verifica tus credenciales.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #38b2ac 0%, #319795 100%)',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.05);
              opacity: 0.9;
            }
          }
          
          @keyframes heartBeat {
            0%, 100% {
              transform: scale(1);
            }
            25% {
              transform: scale(1.2);
            }
            50% {
              transform: scale(1);
            }
          }
          
          @keyframes shimmer {
            0% {
              background-position: -100% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }
          
          .logo-pulse {
            animation: pulse 2s ease-in-out infinite;
          }
          
          .heart-connector {
            animation: heartBeat 1.5s ease-in-out infinite;
          }
          
          .shimmer-line {
            animation: shimmer 3s linear infinite;
          }
          
          @media (prefers-reduced-motion: reduce) {
            .logo-pulse,
            .heart-connector,
            .shimmer-line {
              animation: none;
            }
          }
        `}
      </style>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        width: '100%',
        maxWidth: 440,
        padding: '48px 40px'
      }}>
        {/* Logo and Title */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          marginBottom: 40 
        }}>
          {/* Logo Row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMonarkaEmail ? 20 : 0,
            marginBottom: 20,
            transition: 'gap 0.3s ease'
          }}>
            {/* Eternal Logo */}
            <img 
              src={logo} 
              alt="Sissi Clinic" 
              className={isMonarkaEmail ? 'logo-pulse' : ''}
              style={{ 
                width: 60, 
                height: 60,
                filter: 'drop-shadow(0 4px 8px rgba(56, 178, 172, 0.2))',
                transition: 'all 0.3s ease'
              }} 
            />
            
            {/* Connector with Heart */}
            {isMonarkaEmail && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4
              }}>
                {/* Shimmer line */}
                <div 
                  className="shimmer-line"
                  style={{
                    width: 40,
                    height: 2,
                    background: 'linear-gradient(90deg, transparent, rgba(56, 178, 172, 0.6), rgba(219, 112, 147, 0.6), transparent)',
                    backgroundSize: '200% 100%',
                    borderRadius: 1
                  }}
                />
                {/* Heart */}
                <div 
                  className="heart-connector"
                  style={{
                    fontSize: 16,
                    color: '#db7093',
                    filter: 'drop-shadow(0 2px 4px rgba(219, 112, 147, 0.3))'
                  }}
                >
                  ♥
                </div>
                {/* Shimmer line */}
                <div 
                  className="shimmer-line"
                  style={{
                    width: 40,
                    height: 2,
                    background: 'linear-gradient(90deg, transparent, rgba(219, 112, 147, 0.6), rgba(56, 178, 172, 0.6), transparent)',
                    backgroundSize: '200% 100%',
                    borderRadius: 1
                  }}
                />
              </div>
            )}
            
            {/* Monarka Logo */}
            {isMonarkaEmail && (
              <img 
                src={monarkaLogo} 
                alt="Monarka Clinic" 
                className="logo-pulse"
                style={{ 
                  width: 70, 
                  height: 80,
                  objectFit: 'cover',
                  objectPosition: 'left center',
                  filter: 'drop-shadow(0 4px 8px rgba(219, 112, 147, 0.2))',
                  borderRadius: 8
                }} 
              />
            )}
          </div>
          
          <h1 style={{ 
            margin: 0, 
            fontSize: 28, 
            fontWeight: 700, 
            color: '#1f2937',
            marginBottom: 8,
            transition: 'color 0.3s ease'
          }}>
            {isMonarkaEmail ? 'Monarka Clinic' : 'Sissi Clinic'}
          </h1>
          <p style={{ 
            margin: 0, 
            fontSize: 15, 
            color: '#6b7280',
            textAlign: 'center'
          }}>
            Inicia sesión para continuar
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} style={{ 
          display: 'flex', 
          flexDirection: 'column'
        }}>
          {/* Email/Username Field */}
          <label style={{ 
            marginBottom: 20,
            display: 'block'
          }}>
            <span style={{ 
              display: 'block',
              fontSize: 14,
              fontWeight: 600,
              color: '#374151',
              marginBottom: 8
            }}>
              Email o Usuario
            </span>
            <input
              type="text"
              placeholder="tu@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={isLoading}
              style={{ 
                width: '100%',
                padding: '12px 16px',
                fontSize: 15,
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                outline: 'none',
                transition: 'all 0.2s',
                boxSizing: 'border-box',
                background: isLoading ? '#f9fafb' : '#fff'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#38b2ac';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(56, 178, 172, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </label>

          {/* Password Field */}
          <label style={{ 
            marginBottom: 24,
            display: 'block'
          }}>
            <span style={{ 
              display: 'block',
              fontSize: 14,
              fontWeight: 600,
              color: '#374151',
              marginBottom: 8
            }}>
              Contraseña
            </span>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              disabled={isLoading}
              style={{ 
                width: '100%',
                padding: '12px 16px',
                fontSize: 15,
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                outline: 'none',
                transition: 'all 0.2s',
                boxSizing: 'border-box',
                background: isLoading ? '#f9fafb' : '#fff'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#38b2ac';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(56, 178, 172, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </label>

          {/* Error Message */}
          {error && (
            <div style={{ 
              background: '#fee2e2',
              color: '#dc2626',
              padding: '12px 16px',
              borderRadius: 8,
              fontSize: 14,
              marginBottom: 20,
              border: '1px solid #fecaca'
            }}>
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button 
            type="submit" 
            disabled={isLoading}
            style={{ 
              width: '100%',
              padding: '14px',
              fontSize: 16,
              fontWeight: 600,
              color: '#fff',
              background: isLoading ? '#81c9c4' : '#38b2ac',
              border: 'none',
              borderRadius: 8,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(56, 178, 172, 0.2)'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.background = '#319795';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(56, 178, 172, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.currentTarget.style.background = '#38b2ac';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(56, 178, 172, 0.2)';
              }
            }}
          >
            {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>

        {/* Footer */}
        <div style={{ 
          marginTop: 32,
          paddingTop: 24,
          borderTop: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <p style={{ 
            margin: 0,
            fontSize: 13,
            color: '#9ca3af'
          }}>
            © 2026 Sissi Clinic. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login; 